"use client";

import { useEffect } from "react";

// Periodically asks the server for the current build ID and reloads when it
// differs from the one baked into the running bundle. Catches the case where
// a user's browser is holding an old HTML+JS combo from before a deploy and
// would otherwise keep showing the stale (potentially broken) build.
//
// Trigger points:
//   - mount (catches users coming back to a long-open tab)
//   - tab becomes visible (catches phones waking from lock screen)
//   - every 5 minutes for tabs that stay open
//
// Loop protection: if we already tried to reload twice in 60s we stop — the
// problem is then something other than a stale build and the user should see
// the actual error.
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const SESSION_KEY = "build-version-reload-attempts";
const MAX_ATTEMPTS = 2;
const RESET_AFTER_MS = 60_000;

function canReload(): boolean {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const now = Date.now();
    const state = raw
      ? (JSON.parse(raw) as { count: number; first: number })
      : null;
    if (
      state &&
      now - state.first < RESET_AFTER_MS &&
      state.count >= MAX_ATTEMPTS
    ) {
      return false;
    }
    const next =
      state && now - state.first < RESET_AFTER_MS
        ? { count: state.count + 1, first: state.first }
        : { count: 1, first: now };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
  } catch {
    // sessionStorage unavailable (private mode) — allow reload anyway.
  }
  return true;
}

export function BuildVersionWatcher() {
  useEffect(() => {
    const clientBuildId = process.env.NEXT_PUBLIC_BUILD_ID;
    if (!clientBuildId) return;

    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch("/api/version", { cache: "no-store" });
        if (!res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        if (cancelled) return;
        if (
          data.buildId &&
          data.buildId !== "unknown" &&
          data.buildId !== clientBuildId
        ) {
          if (canReload()) window.location.reload();
        }
      } catch {
        // Network blip — try again next interval.
      }
    };

    void check();
    const interval = window.setInterval(check, POLL_INTERVAL_MS);

    const onVisibility = () => {
      if (!document.hidden) void check();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return null;
}
